import {BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate} from "react-router-dom";
import {Box, CircularProgress, Typography} from "@mui/material";
import MainMenu from "./app/MainMenu/MainMenu";
import {GlobalStateProvider, useGlobalOrder} from './utils/GlobalStateContext';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {lazy, Suspense, useEffect, useRef} from "react";
import {KioskSessionProvider} from "./kiosk/context/KioskSessionContext";
import {restartApplication} from "./utils/restartApplication";
import {getNativeAppState, isNativeApp, onNativeMessage, postToNative, setNativeAppState} from "./utils/nativeBridge";
import {PosQuickActionsFab} from "./app/QuickActions/PosQuickActions";

const OrderPage = lazy(() => import("./app/OrderPage/OrderPage"));
const OrderHistory = lazy(() => import("./app/OrderHistory/OrderHistory"));
const Inventory = lazy(() => import("./app/Inventory/Inventory"));
const PrepManagement = lazy(() => import("./app/PrepManagement/PrepManagement"));
const SalesRecords = lazy(() => import("./app/Sales_Records/SalesRecords"));
const DiningOut = lazy(() => import("./app/DiningOut/DiningOut"));
const Income = lazy(() => import("./app/Income/Income"));
const SopChecklist = lazy(() => import("./app/SopChecklist/SopChecklist"));
const LuckyWheel = lazy(() => import("./app/LuckyWheel/LuckyWheel"));
const Purchase = lazy(() => import("./app/Purchase/Purchase"));
const Waste = lazy(() => import("./app/Waste/Waste"));
const Chat = lazy(() => import("./app/Chat/Chat"));
const ReservationPage = lazy(() => import("./app/Reservations/ReservationPage"));

function RouteFallback() {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                minHeight: "60vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                backgroundColor: "#FFFBEB",
            }}
        >
            <CircularProgress size={28} sx={{color: "#1C2D42"}}/>
            <Typography sx={{color: "#536073", fontSize: 14, fontWeight: 700}}>
                載入中
            </Typography>
        </Box>
    );
}

// 定義 AutoReload 組件
// 更新機制（iPad 引導模式下頁面長時間不關，靠這裡拿新版本）：
// 1. 閒置 1 小時自動重整（kiosk 自助點餐除外，避免打斷客人）
// 2. 待機超過 5 分鐘後喚醒時重整
// 3. 每天凌晨 4 點後強制重整一次（所有模式，含 kiosk）
// 4. 每 10 分鐘比對 asset-manifest.json，發現新版本後等操作閒置 5 分鐘再重整
function AutoReload() {
    const {systemData} = useGlobalOrder();
    const idleTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const hiddenAtRef = useRef(null);
    const hasNewVersionRef = useRef(false);
    const idleTimeLimit = 60 * 60 * 1000; // 設定閒置時間為1個小時

    const resetIdleTimer = () => {
        lastActivityRef.current = Date.now();
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
        }
        idleTimeoutRef.current = setTimeout(() => {
            window.location.reload(); // 閒置超時後重新整理頁面
        }, idleTimeLimit);
    };

    // 檢測頁面是否被喚醒（移動裝置待機後喚醒）；待機超過 5 分鐘才重整，避免短暫切換造成畫面閃跳
    const handleVisibilityChange = () => {
        const hidden = document.visibilityState === "hidden" || (isNativeApp() && getNativeAppState() === "background");
        if (hidden) {
            if (hiddenAtRef.current) return;
            hiddenAtRef.current = Date.now();
            return;
        }
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) {
            window.location.reload();
        }
    };

    // 每天凌晨 4 點後強制重整一次（localStorage 記日期，跨睡眠/斷線也只會執行一次）
    useEffect(() => {
        const DAILY_KEY = "last_auto_reload_date";
        const todayKey = (date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

        // 剛載入即是最新版本，今天不需再重整
        const now = new Date();
        if (now.getHours() >= 4 && localStorage.getItem(DAILY_KEY) !== todayKey(now)) {
            localStorage.setItem(DAILY_KEY, todayKey(now));
        }

        const timerId = setInterval(() => {
            const current = new Date();
            if (current.getHours() < 4) return;
            if (localStorage.getItem(DAILY_KEY) === todayKey(current)) return;
            localStorage.setItem(DAILY_KEY, todayKey(current));
            window.location.reload();
        }, 60 * 1000);

        return () => clearInterval(timerId);
    }, []);

    // 定時比對 asset-manifest.json 的 main.js hash，偵測到新部署後等閒置再重整（kiosk 只靠每日重整）
    useEffect(() => {
        if (systemData?.client_mode === "kiosk") return undefined;
        const currentMain = document.querySelector('script[src*="/static/js/main."]')?.getAttribute("src");
        if (!currentMain) return undefined; // 開發模式沒有 hash bundle，跳過

        const checkVersion = async () => {
            try {
                const res = await fetch(`/asset-manifest.json?_=${Date.now()}`, {cache: "no-store"});
                if (!res.ok) return;
                const manifest = await res.json();
                const latestMain = manifest?.files?.["main.js"];
                if (latestMain && latestMain !== currentMain) {
                    hasNewVersionRef.current = true;
                }
            } catch (error) {
                // 離線或部署中，下次再檢查
            }
        };

        const versionTimerId = setInterval(checkVersion, 10 * 60 * 1000);
        const reloadTimerId = setInterval(() => {
            if (hasNewVersionRef.current && Date.now() - lastActivityRef.current > 5 * 60 * 1000) {
                restartApplication();
            }
        }, 60 * 1000);

        return () => {
            clearInterval(versionTimerId);
            clearInterval(reloadTimerId);
        };
    }, [systemData?.client_mode]);

    useEffect(() => {
        if (systemData?.client_mode === "kiosk") return undefined;
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

        events.forEach((event) => {
            window.addEventListener(event, resetIdleTimer);
        });
        document.addEventListener("visibilitychange", handleVisibilityChange);

        resetIdleTimer(); // 初始化計時器

        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, resetIdleTimer);
            });
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current);
            }
        };
    }, [systemData?.client_mode]);
    return null; // 不需要渲染任何內容
}

// 網頁 ↔ Expo App 橋接（spec §4.3）。非 App 環境整個元件不做事。
function NativeBridgeHost() {
    const location = useLocation();
    const navigate = useNavigate();
    const readySentRef = useRef(false);

    useEffect(() => {
        if (!isNativeApp()) return;
        if (!readySentRef.current) {
            readySentRef.current = true;
            postToNative('ready', {path: location.pathname});
        }
        postToNative('route', {path: location.pathname});
    }, [location.pathname]);

    useEffect(() => {
        if (!isNativeApp()) return undefined;
        return onNativeMessage((msg) => {
            switch (msg.type) {
                case 'navigate':
                    if (typeof msg.payload.path === 'string') navigate(msg.payload.path, {replace: true}); // 功能切換不堆疊歷史，避免返回鍵在殘留頁面間跳
                    break;
                case 'setAuth': {
                    const {token, account, _ac} = msg.payload;
                    if (token) localStorage.setItem('token', token);
                    if (account) localStorage.setItem('account', account);
                    if (_ac) localStorage.setItem('_ac', _ac);
                    // 不重載：api_client 每次請求才讀 localStorage 的 token
                    break;
                }
                case 'clearAuth':
                    localStorage.removeItem('token');
                    localStorage.removeItem('_ac');
                    window.location.replace('/'); // 整頁重載，MainMenu 才會重新判斷登入並斷開 WebSocket
                    break;
                case 'appState':
                    // AutoReload 靠 visibilitychange 判斷待機；WebView 內由原生告知
                    setNativeAppState(msg.payload.state);
                    document.dispatchEvent(new Event('visibilitychange'));
                    break;
                default:
                    console.warn('未處理的原生訊息:', msg.type);
            }
        });
    }, [navigate]);

    return null;
}

function App() {

    return (
        <GlobalStateProvider>
            <KioskSessionProvider>
              <Router>
                <ToastContainer
                    position="top-center"
                    autoClose={3600}
                    hideProgressBar={false}
                    closeOnClick
                    draggable
                    pauseOnHover
                    theme="light"
                    newestOnTop
                    limit={3}
                    toastClassName="app-toast"
                    bodyClassName="app-toast-body"
                    progressClassName="app-toast-progress"
                />
                <NativeBridgeHost/>
                <AutoReload/> {/* 加入 AutoReload 組件 */}
                <PosQuickActionsFab/>
                <Suspense fallback={<RouteFallback/>}>
                    <Routes>
                        <Route path="/" element={<MainMenu/>}>
                            <Route path="/order" element={<OrderPage/>}/>
                            <Route path="/order-history" element={<OrderHistory/>}/>
                            <Route path="/reservations" element={<ReservationPage/>}/>
                            <Route path="/chat" element={<Chat/>}/>
                            <Route path="/inventory" element={<Inventory/>}/>
                            {/* 盤點叫貨已併入營業資訊頁籤，舊網址導過去 */}
                            <Route path="/inventory-check" element={<Navigate to="/income?tab=inventory" replace/>}/>
                            <Route path="/purchase" element={<Purchase/>}/>
                            <Route path="/waste" element={<Waste/>}/>
                            <Route path="/prep-management" element={<PrepManagement/>}/>
                            <Route path="/sales-records" element={<SalesRecords/>}/>
                            <Route path="/dining-out" element={<DiningOut/>}/>
                            <Route path="/income" element={<Income/>}/>
                            <Route path="/sop" element={<SopChecklist/>}/>
                            <Route path="/lucky-wheel" element={<LuckyWheel/>}/>
                            <Route path={"/logout"} element ={<Logout/>}/>
                            <Route path="kiosk/*" element={null}/>
                        </Route>
                    </Routes>
                </Suspense>
              </Router>
            </KioskSessionProvider>
        </GlobalStateProvider>
    );
}

function Logout() {
    const navigate = useNavigate();
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return; // StrictMode 開發模式 effect 會跑兩次
        ranRef.current = true;
        if (window.confirm("確定是否登出?")) {
            localStorage.removeItem('token'); // 移除 token
            localStorage.removeItem('_ac'); // 移除自動重登用的帳密
            postToNative('logout');
            window.location.replace('/'); // 整頁重載回登入頁，App 內原生收到 logout 會接手
        } else {
            navigate('/', {replace: true});
        }
    }, [navigate]);

    return null;
}

export default App;
